import React, {useMemo} from 'react';
import {StyleSheet, View} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import type {ReportsStackParamList} from '@/app/routes';
import {fetchControlDepartments} from '@/entities/attendance/api';
import {
  currentMonth,
  periodKey,
  periodParam,
  type Period,
} from '@/entities/attendance/period';
import {useSalaryAccess} from '@/entities/permission/access';
import {exportPayroll, fetchPayroll} from '@/entities/salary/api';
import type {PayrollRow} from '@/entities/salary/types';
import {useAuthStore} from '@/entities/session/auth-store';
import {reportFileName} from '@/features/view-attendance/lib/csv';
import {
  describePeriod,
  usePeriodLabel,
} from '@/features/view-attendance/lib/period-label';
import {ExportBar} from '@/features/view-attendance/ui/ExportBar';
import {ReportToolbar} from '@/features/view-attendance/ui/ReportToolbar';
import {PayrollList} from '@/features/view-payroll/ui/PayrollList';
import {PayrollTotals} from '@/features/view-payroll/ui/PayrollTotals';
import {ContextPlaceholder} from '@/widgets/context-placeholder/ContextPlaceholder';
import {ApiError} from '@/shared/api';
import {listParam, useParamState} from '@/shared/lib/use-param-state';
import {Banner} from '@/shared/ui/banner';
import {EmptyState} from '@/shared/ui/empty-state';
import {Page} from '@/shared/ui/page';
import {Segmented} from '@/shared/ui/segmented';
import {Section} from '@/shared/ui/section';
import {Skeleton} from '@/shared/ui/skeleton';
import {radii, spacing} from '@/shared/ui/theme';

/**
 * «Ведомость» — сколько начислено по явке за период.
 *
 * ## Что этот экран НЕ показывает, и почему это написано на нём самом
 *
 * Ведомость не знает отпусков, больничных, авансов, налогов и удержаний. Пропуск — это
 * неявка, и она не оплачивается. То есть это свод начисленного по явке, а не расчётный
 * лист, — и предупреждение стоит НАД цифрами, а не под ними: бухгалтер, принявший одно
 * за другое, узнает об ошибке не на этом экране.
 *
 * ## Право
 *
 * Раздел виден только при `salaries/read`. Это одно из двух мест в кабинете, где
 * доступ проверяется ДО запроса, а не выясняется из `403` (второе — «Оплата» в карточке
 * человека): у ролей «руководитель отдела» и «наблюдатель» доступ сюда запрещён явно,
 * то есть `403` здесь — штатное состояние, а не сбой, и встречать его пустой таблицей
 * с прочерками значит сообщать им ровно то, что запрет и должен скрыть.
 *
 * Экран досягаем по прямой ссылке (`/payroll`), поэтому проверка живёт здесь, а не
 * только в строке меню, которая на него ведёт.
 *
 * ## Период
 *
 * Тот же `ReportToolbar`, что и у отчётов явки, и то же разбиение периода на сервере —
 * «прошлый месяц» обязано значить здесь ровно то же, что там, иначе две цифры за один
 * и тот же «прошлый месяц» разойдутся и объяснить это будет нечем.
 */
type Props = NativeStackScreenProps<ReportsStackParamList, 'Payroll'>;

/** Стабильная пустая выборка — см. тот же комментарий на «Посещаемости». */
const ALL_DEPARTMENTS: string[] = [];

const styles = StyleSheet.create({
  segment: {alignSelf: 'flex-start', minWidth: 260},
  loading: {gap: spacing(3)},
});

export function PayrollScreen({navigation}: Props) {
  const {t, i18n} = useTranslation();
  const {label: periodTitle} = usePeriodLabel();
  const companyId = useAuthStore(s => s.activeContext?.companyId);
  const access = useSalaryAccess(companyId);

  // Прибито один раз на монтирование: пресеты разрешаются относительно него, поэтому
  // ведомость не может тихо переехать на другой период, если приложение оставили
  // открытым через полночь.
  const now = useMemo(() => new Date(), []);

  // Тот же довод, что и на «Посещаемости»: период и отделы — это вопрос, а вопрос
  // живёт в адресе. См. `shared/lib/use-param-state`.
  const thisMonth = useMemo(() => currentMonth(now), [now]);
  const periodCodec = useMemo(() => periodParam(now, thisMonth), [now, thisMonth]);
  const [period, setPeriod] = useParamState<Period>('period', thisMonth, periodCodec);
  const [departmentIds, setDepartmentIds] = useParamState(
    'departments',
    ALL_DEPARTMENTS,
    listParam,
  );

  const departments = useQuery({
    queryKey: ['control-departments', companyId],
    queryFn: () => fetchControlDepartments(companyId as string),
    enabled: !!companyId,
  });

  const report = useQuery({
    queryKey: [
      'payroll',
      companyId,
      periodKey(period),
      departmentIds.join(','),
    ],
    queryFn: () =>
      fetchPayroll(companyId as string, period, {departmentIds}),
    enabled: !!companyId && access.canRead,
  });

  if (!companyId) {
    return <ContextPlaceholder titleKey="payroll.title" />;
  }

  const rows: PayrollRow[] = report.data?.rows ?? [];
  const totals = report.data?.totals ?? [];
  const skipped = report.data?.skipped_rows ?? 0;
  const periodLabel = periodTitle(report.data ?? describePeriod(period));
  const deptList = departments.data?.departments ?? [];

  const fileStem = reportFileName(
    [t('payroll.fileName'), periodLabel],
    '',
  ).slice(0, -1);

  /** Отчёт `403` не отдаёт, если право есть; но право могли отозвать посреди сессии. */
  const forbidden = report.error instanceof ApiError && report.error.status === 403;

  const openPerson = (row: PayrollRow) =>
    // Откреплённого в «Персонале» нет — карточки у него нет, есть отчёт за дни
    // до открепления. Он в этом же стеке.
    row.detached_at
      ? navigation.navigate('EmployeeReport', {
          recordId: row.record_id,
          name: row.full_name,
          period: periodKey(period),
        })
      : // Межвкладочный переход: карточка человека живёт в стеке «Персонал». Через
        // `getParent()` и с проверкой — у персоны «сотрудник» этой вкладки нет вовсе, и
        // отсутствующий родитель не должен ронять тап.
        navigation.getParent()?.navigate('Personnel', {
          screen: 'PersonnelCard',
          params: {recordId: row.record_id, name: row.full_name},
        });

  const exportSlot = (
    <ExportBar
      fileName={fileStem}
      contents={t('payroll.exportContents', {
        period: periodLabel,
        count: rows.length,
      })}
      onServer={format =>
        exportPayroll(companyId, period, format, i18n.language, {departmentIds})
      }
    />
  );

  let content: React.ReactNode;

  if (access.isPending) {
    content = <Skeleton height={190} radius={radii.xl} />;
  } else if (!access.canRead || forbidden) {
    // Ровно то, что запрет и означает: раздела для этого человека нет. Без таблицы,
    // без итога и без намёка на то, что в ней могло бы быть.
    content = (
      <EmptyState
        icon="lock"
        title={t('payroll.forbiddenTitle')}
        hint={t('payroll.forbidden')}
        testID="payroll-forbidden"
      />
    );
  } else if (report.isPending) {
    content = (
      <View style={styles.loading}>
        <Skeleton height={150} radius={radii.xl} />
        <Skeleton height={190} radius={radii.xl} />
      </View>
    );
  } else if (report.isError) {
    content = (
      <EmptyState
        tone="error"
        title={t('payroll.error')}
        actionLabel={t('actions.retry')}
        onAction={() => void report.refetch()}
      />
    );
  } else {
    content = (
      <>
        <Section>
          <PayrollTotals
            totals={totals}
            skipped={skipped}
            counted={Math.max(0, rows.length - skipped)}
          />
        </Section>
        <Section
          title={t('payroll.rows')}
          hint={t('payroll.rowsHint', {count: rows.length})}>
          <PayrollList rows={rows} companyId={companyId} onOpen={openPerson} />
        </Section>
      </>
    );
  }

  return (
    <Page
      scroll
      title={t('payroll.title')}
      subtitle={
        report.data
          ? [periodLabel, t('payroll.rowsHint', {count: rows.length})].join('  \u00b7  ')
          : t('payroll.subtitle')
      }
      refreshing={report.isRefetching}
      onRefresh={() => void report.refetch()}
      testID="payroll-screen"
      toolbar={
        <>
          {/* Тот же переключатель, что и на «Посещаемости»: явка и начисления —
              две проекции одного периода, а не два раздела меню. */}
          <Segmented
            style={styles.segment}
            value="payroll"
            onChange={value => {
              if (value === 'attendance') {
                navigation.navigate('ReportsOverview');
              }
            }}
            testID="attendance-mode"
            options={[
              {value: 'attendance', label: t('attendance.tabAttendance')},
              {value: 'payroll', label: t('payroll.title')},
            ]}
          />

          {/* Над цифрами, а не под ними: см. модульный комментарий. */}
          <Banner
            tone="info"
            title={t('payroll.notPayslipTitle')}
            message={t('payroll.notPayslip')}
            testID="payroll-disclaimer"
          />

          {access.canRead ? (
            <ReportToolbar
              period={period}
              onPeriodChange={setPeriod}
              now={now}
              periodLabel={periodLabel}
              departments={deptList}
              departmentIds={departmentIds}
              onDepartmentsChange={setDepartmentIds}
              exportSlot={exportSlot}
            />
          ) : null}
        </>
      }>
      {content}
    </Page>
  );
}
